import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { SearchOutlined, SendOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder, SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import TrashIcon from "../../assets/icons/trash.svg";
import PenIcon from "../../assets/icons/pen.svg";
import PhoneNumberDisplay from "../PhoneNumberDisplay";
import SmsTestService, {
  ISmsTest,
  ISmsTestPayload,
  SmsTestStatus,
} from "../../services/SmsTestService";
import { formatDate, handlePhonePaste, openNotification } from "../../utils";
import { getSmsTestStatusMeta, SMS_TEST_PHONE_PATTERN } from "./smsTestUtils";

const { Title, Text } = Typography;

type DateSortField = "createdAt" | "updatedAt" | "lastSentAt";
type TableSortOrder = "ascend" | "descend" | null;

const SmsTestList: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [items, setItems] = useState<ISmsTest[]>([]);
  const [editorDrawerVisible, setEditorDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ISmsTest | null>(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<DateSortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<TableSortOrder>("descend");
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 400);

    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (pagination.current !== 1) {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
  }, [debouncedSearch]);

  const buildParams = useCallback(() => {
    const resolvedSort =
      sortField && sortOrder
        ? `${sortField},${sortOrder === "ascend" ? "asc" : "desc"}`
        : "createdAt,desc";

    const params: Record<string, any> = {
      page: pagination.current - 1,
      size: pagination.pageSize,
      sort: resolvedSort,
    };

    if (debouncedSearch) {
      params["search.contains"] = debouncedSearch;
    }

    return params;
  }, [debouncedSearch, pagination.current, pagination.pageSize, sortField, sortOrder]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await SmsTestService.list(buildParams());
      setItems(response.data.content);
      setPagination((prev) => ({
        ...prev,
        total: response.data.totalElements ?? 0,
      }));
    } catch (error) {
      console.error("Sms tests could not be loaded", error);
      openNotification("error", "Hata", "SMS test kayıtları listelenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const closeEditorDrawer = () => {
    setEditorDrawerVisible(false);
    setEditingItem(null);
    setSubmitLoading(false);
    form.resetFields();
  };

  const openCreateDrawer = () => {
    setEditingItem(null);
    form.resetFields();
    setEditorDrawerVisible(true);
  };

  const openEditDrawer = (record: ISmsTest) => {
    setEditingItem(record);
    form.setFieldsValue({
      fullName: record.fullName,
      phoneNumber: record.phoneNumber,
      message: record.message,
    });
    setEditorDrawerVisible(true);
  };

  const handleSubmit = async (values: ISmsTestPayload) => {
    const payload: ISmsTestPayload = {
      fullName: String(values.fullName ?? "").trim(),
      phoneNumber: String(values.phoneNumber ?? "").trim(),
      message: String(values.message ?? "").trim(),
    };

    setSubmitLoading(true);
    try {
      if (editingItem) {
        await SmsTestService.update(editingItem.id, payload);
        openNotification("success", "Başarılı", "SMS test kaydı güncellendi.");
      } else {
        await SmsTestService.create(payload);
        openNotification("success", "Başarılı", "SMS test kaydı oluşturuldu.");
      }

      closeEditorDrawer();
      fetchList();
    } catch (error: any) {
      console.error("Sms test save failed", error);
      const errorMessage = error?.response?.data?.message || "Kaydetme sırasında hata oluştu.";
      openNotification("error", "Hata", errorMessage);
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (record: ISmsTest) => {
    setActionLoadingKey(`delete-${record.id}`);
    try {
      await SmsTestService.remove(record.id);
      openNotification("success", "Başarılı", "SMS test kaydı silindi.");
      fetchList();
    } catch (error: any) {
      console.error("Sms test delete failed", error);
      const errorMessage = error?.response?.data?.message || "Silme sırasında hata oluştu.";
      openNotification("error", "Hata", errorMessage);
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleSend = async (record: ISmsTest) => {
    setActionLoadingKey(`send-${record.id}`);
    try {
      const response = await SmsTestService.send(record.id);
      const updated = response.data;

      if (updated.status === "SUCCESS") {
        openNotification("success", "Başarılı", "SMS başarıyla gönderildi.");
      } else {
        openNotification(
          "error",
          "Başarısız",
          updated.lastErrorMessage || "SMS gönderimi başarısız oldu.",
        );
      }

      fetchList();
    } catch (error: any) {
      console.error("Sms test send failed", error);
      const errorMessage = error?.response?.data?.message || "SMS gönderimi sırasında hata oluştu.";
      openNotification("error", "Başarısız", errorMessage);
    } finally {
      setActionLoadingKey(null);
    }
  };

  const currentSortOrder = (field: DateSortField): SortOrder | undefined => {
    if (sortField !== field) {
      return undefined;
    }
    return sortOrder ?? undefined;
  };

  const handleTableChange = (
    nextPagination: TablePaginationConfig,
    _filters: Record<string, any>,
    sorter: SorterResult<ISmsTest> | SorterResult<ISmsTest>[],
  ) => {
    const resolvedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField =
      resolvedSorter?.field === "createdAt" ||
      resolvedSorter?.field === "updatedAt" ||
      resolvedSorter?.field === "lastSentAt"
        ? (resolvedSorter.field as DateSortField)
        : "createdAt";
    const nextOrder = (resolvedSorter?.order ?? "descend") as TableSortOrder;

    setSortField(nextField);
    setSortOrder(nextOrder);
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPagination.pageSize ?? prev.pageSize,
    }));
  };

  const columns: ColumnsType<ISmsTest> = useMemo(
    () => [
      {
        title: "Ad Soyad",
        dataIndex: "fullName",
        key: "fullName",
      },
      {
        title: "Telefon",
        dataIndex: "phoneNumber",
        key: "phoneNumber",
        render: (value: string) => <PhoneNumberDisplay value={value} />,
      },
      {
        title: "Mesaj",
        dataIndex: "message",
        key: "message",
        render: (value: string) => (
          <Tooltip title={value}>
            <Text ellipsis style={{ maxWidth: 280, display: "inline-block" }}>
              {value}
            </Text>
          </Tooltip>
        ),
      },
      {
        title: "Durum",
        dataIndex: "status",
        key: "status",
        render: (value: SmsTestStatus, record: ISmsTest) => {
          const meta = getSmsTestStatusMeta(value);
          const tag = <Tag color={meta.color}>{meta.label}</Tag>;

          if (value === "FAILED" && record.lastErrorMessage) {
            return <Tooltip title={record.lastErrorMessage}>{tag}</Tooltip>;
          }

          return tag;
        },
      },
      {
        title: "Harici Referans",
        dataIndex: "externalId",
        key: "externalId",
        render: (value?: string | null) => value || "-",
      },
      {
        title: "Son Gönderim Tarihi",
        dataIndex: "lastSentAt",
        key: "lastSentAt",
        sorter: true,
        sortOrder: currentSortOrder("lastSentAt"),
        render: (value?: string | null) => (value ? formatDate(value) : "-"),
      },
      {
        title: "Oluşturma Tarihi",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortOrder: currentSortOrder("createdAt"),
        render: (value?: string) => (value ? formatDate(value) : "-"),
      },
      {
        title: "Güncelleme Tarihi",
        dataIndex: "updatedAt",
        key: "updatedAt",
        sorter: true,
        sortOrder: currentSortOrder("updatedAt"),
        render: (value?: string) => (value ? formatDate(value) : "-"),
      },
      {
        title: "",
        key: "actions",
        align: "right",
        render: (_: unknown, record: ISmsTest) => (
          <Space size={12}>
            <Tooltip title="Düzenle">
              <Button
                shape="circle"
                className="icon-button"
                onClick={() => openEditDrawer(record)}
                icon={<img src={PenIcon} alt="pen" className="icon" />}
              />
            </Tooltip>
            <Tooltip title="Gönder">
              <Button
                type="primary"
                className="d-button width-auto"
                icon={<SendOutlined />}
                loading={actionLoadingKey === `send-${record.id}`}
                onClick={() => handleSend(record)}
              >
                Gönder
              </Button>
            </Tooltip>
            <Popconfirm
              title="Bu SMS test kaydını silmek istediğinize emin misiniz?"
              onConfirm={() => handleDelete(record)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Tooltip title="Sil">
                <Button
                  shape="circle"
                  className="icon-button"
                  loading={actionLoadingKey === `delete-${record.id}`}
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [actionLoadingKey, sortField, sortOrder],
  );

  return (
    <Card className="br-30px">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div>
          <Row justify="space-between" align="middle" className="mb-16px">
            <div>
              <Title style={{ margin: 0, display: "inline-block" }} level={4}>
                SMS Test
              </Title>
              <Text type="secondary" className="ms-5">
                <strong>({pagination.total})</strong> kayıt listelendi
              </Text>
            </div>
            <Row align="middle">
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: "#b8b9bc" }} />}
                className="d-input d-input-form d-input-filter ml-16px"
                style={{ width: 220 }}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={openCreateDrawer}
              >
                Yeni SMS Test Kaydı
              </Button>
            </Row>
          </Row>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
          }}
        />
      </Space>

      <Drawer
        title={editingItem ? "SMS Test Kaydını Düzenle" : "Yeni SMS Test Kaydı"}
        open={editorDrawerVisible}
        onClose={closeEditorDrawer}
        width={460}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            fullName: "",
            phoneNumber: "",
            message: "",
          }}
        >
          <Form.Item
            name="fullName"
            label="Ad Soyad"
            rules={[
              { required: true, message: "Ad Soyad zorunludur." },
              { max: 120, message: "Ad Soyad en fazla 120 karakter olabilir." },
            ]}
          >
            <Input maxLength={120} />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="Telefon"
            rules={[
              { required: true, message: "Telefon zorunludur." },
              { pattern: SMS_TEST_PHONE_PATTERN, message: "Telefon 5 ile başlayan 10 haneli bir numara olmalıdır." },
            ]}
          >
            <Input
              addonBefore="+90"
              maxLength={10}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              onPaste={(event) => handlePhonePaste(event, { form, fieldName: "phoneNumber" })}
            />
          </Form.Item>

          <Form.Item
            name="message"
            label="Mesaj"
            rules={[
              { required: true, message: "Mesaj zorunludur." },
              { max: 1000, message: "Mesaj en fazla 1000 karakter olabilir." },
            ]}
          >
            <Input.TextArea rows={5} maxLength={1000} showCount />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button block type="primary" htmlType="submit" loading={submitLoading}>
              {editingItem ? "Güncelle" : "Kaydet"}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};

export default SmsTestList;
